import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { providerSignupRequest, registerRequest } from 'store/signup/actions';
import {
  fetchBusinessSizesRequest,
  fetchBusinessTypesRequest,
  fetchCountriesRequest,
  fetchCountriesExternalDataRequest,
} from 'store/common/actions';
import { getSignUpLoading } from 'store/signup/selectors';
import {
  getCommonBusinessSizes,
  getCommonBusinessTypes,
  getCommonCountries,
  getCommonLoading,
  getCountriesExternalData,
} from 'store/common/selectors';
import { SignUpForm } from 'components/SignUpForm';
import { UserDetails } from 'store/signup/models';
import { RegisterRequestAction } from 'store/signup/types';
import { Helmet } from 'react-helmet-async';
import { Country } from 'store/common/models';
import { Provider } from 'store/api/enums';
import { Form } from 'antd';
import { getCookie } from 'utils/get-cookie';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { MessageStates } from 'containers/MessageBox';
import { turnMessageOn } from 'store/app/actions';
import type { MessageData } from 'store/app/types';
import { SSO_SIGNUP_PROVIDER_KEY } from 'utils/sso-signup-provider';

interface SsoValues {
  userFirstName: string;
  userLastName: string;
  userEmail: string;
  subIdentifier: string;
  realmId: string;
  tokenCode: string;
  emailVerified: string;
  provider?: string;
}

const parseSsoPayload = (raw: string | null): SsoValues | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      userFirstName: parsed.userFirstName ?? '',
      userLastName: parsed.userLastName ?? '',
      userEmail: parsed.userEmail ?? '',
      subIdentifier: parsed.subIdentifier ?? '',
      realmId: parsed.realmId ?? '',
      tokenCode: parsed.tokenCode ?? '',
      emailVerified: parsed.emailVerified ?? '',
      provider: parsed.provider ?? undefined,
    };
  } catch {
    return null;
  }
};

export const SignUpPage: React.FC = () => {
  const signupLoading = useSelector(getSignUpLoading);
  const countries = useSelector(getCommonCountries);
  const countriesExternalData = useSelector(getCountriesExternalData);
  const businessTypes = useSelector(getCommonBusinessTypes);
  const businessSizes = useSelector(getCommonBusinessSizes);
  const commonLoading = useSelector(getCommonLoading);
  const loading = signupLoading || commonLoading;
  const dispatch = useDispatch();
  const location = useLocation();
  const [form] = Form.useForm();
  const [ssoSignUp, setSsoSignUp] = useState<boolean>(false);
  const [showPage, setShowPage] = React.useState(false);

  const { t } = useTranslation();

  const resolveProviderLabel = (providerKey?: string) => {
    if (!providerKey) {
      return t('main.provider', { defaultValue: 'provider' });
    }
    return t(`common.${providerKey.toLowerCase()}`, {
      defaultValue: providerKey,
    });
  };

  const checkForSSO = () => {
    const ssoCookie = getCookie('SSO-USER-DETAILS');
    const ssoLocal = localStorage.getItem('SSO-USER-DETAILS');
    const storedProvider = localStorage.getItem(SSO_SIGNUP_PROVIDER_KEY);
    let ssoValues: SsoValues | null = null;

    localStorage.removeItem('SSO-USER-DETAILS');
    localStorage.removeItem(SSO_SIGNUP_PROVIDER_KEY);

    ssoValues = parseSsoPayload(ssoCookie) || parseSsoPayload(ssoLocal);

    if (!ssoValues && location.search) {
      const params = new URLSearchParams(location.search);
      const userFirstName = params.get('userFirstName') ?? '';
      const userLastName = params.get('userLastName') ?? '';
      const userEmail = params.get('userEmail') ?? '';
      const subIdentifier = params.get('subIdentifier') ?? '';

      // Prefill whenever any SSO identity fields came back from the provider.
      if (userFirstName || userLastName || userEmail || subIdentifier) {
        ssoValues = {
          userFirstName,
          userLastName,
          userEmail,
          subIdentifier,
          realmId: params.get('realmId') ?? '',
          tokenCode: params.get('tokenCode') ?? '',
          emailVerified: params.get('emailVerified') ?? '',
          provider: params.get('provider') ?? undefined,
        };
      }
    }

    if (ssoValues) {
      form.setFieldsValue({
        ['firstName']: ssoValues.userFirstName,
        ['lastName']: ssoValues.userLastName,
        ['email']: ssoValues.userEmail,
        ['subIdentifier']: ssoValues.subIdentifier,
        ['tokenCode']: ssoValues.tokenCode,
        ['realmId']: ssoValues.realmId,
        ['emailVerified']: ssoValues.emailVerified,
      });
      setSsoSignUp(true);
      const providerLabel = resolveProviderLabel(
        ssoValues.provider || storedProvider || undefined
      );
      const message: MessageData = {
        title: t('signup.almost-there'),
        description: t('signup.sso-connection-success', {
          provider: providerLabel,
        }),
        type: MessageStates.INFO,
      };
      dispatch(turnMessageOn(message));
    }
  };
  const onRegister = (
    user: UserDetails,
    gCaptchaResponse: string
  ): RegisterRequestAction => {
    return dispatch(registerRequest(user, gCaptchaResponse, ssoSignUp));
  };

  const callProviderSSO = (provider: Provider) => {
    localStorage.setItem(SSO_SIGNUP_PROVIDER_KEY, provider);
    const providerFormattedName = t(`common.${provider?.toLowerCase()}`, {
      defaultValue: provider,
    });
    dispatch(providerSignupRequest(provider, providerFormattedName));
  };
  const [scopeCountries, setScopeCountries] = React.useState<Country[]>([]);
  useEffect(() => {
    dispatch(fetchCountriesRequest());
    dispatch(fetchCountriesExternalDataRequest());
    dispatch(fetchBusinessSizesRequest());
    dispatch(fetchBusinessTypesRequest());
    checkForSSO();
    setShowPage(true);
  }, []);

  useEffect(() => {
    if (countries && countries.length > 0) {
      const scopeCnt: Country[] = countries.filter((c) => c.scopeSupported);

      setScopeCountries(scopeCnt);
    }
  }, [countries]);

  return (
    <React.Fragment>
      {showPage && (
        <>
          <Helmet>
            <title>{`${t('common.sign-up')} | ${process.env.PROJECT_NAME}`}</title>
            <meta
              name="description"
              content={t('signup.page-meta-description')}
            />
          </Helmet>

          <div>
            <SignUpForm
              onRegister={onRegister}
              countries={countries}
              countriesExternalData={countriesExternalData}
              businessTypes={businessTypes}
              businessSizes={businessSizes}
              scopeCountries={scopeCountries}
              loading={loading}
              callProviderSSO={callProviderSSO}
              form={form}
              ssoSignUp={ssoSignUp}
            />
          </div>
        </>
      )}
    </React.Fragment>
  );
};
